import { UserRound } from "lucide-react";
import { useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/features/auth/AuthContext";
import { needsProfileName } from "@/features/auth/profileName";
import { sessionApi } from "@/features/auth/session";
import { safeRedirectPath } from "@/lib/errors";
import { Button } from "@/shared/ui/button";
import { ErrorAlert } from "@/shared/ui/error-alert";
import { Input } from "@/shared/ui/input";
import { useI18n } from "@/i18n/I18nContext";

export const DISPLAY_NAME_MAX_LENGTH = 100;

function initialsOf(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => Array.from(part)[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

export function ProfileNamePage() {
  const { t } = useI18n();
  const { user, updateUser, signOut } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [name, setName] = useState("");
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const initials = initialsOf(name);
  const destination = safeRedirectPath(params.get("redirect"), "/meetings");

  const validate = (value: string): string | null => {
    const trimmed = value.trim();
    if (!trimmed) return t("profileName.required");
    // Count characters, not UTF-16 units, so names with emoji or CJK are not cut early.
    if (Array.from(trimmed).length > DISPLAY_NAME_MAX_LENGTH) {
      return t("profileName.tooLong", { max: DISPLAY_NAME_MAX_LENGTH });
    }
    return null;
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    const problem = validate(name);
    setFieldError(problem);
    setSaveError(null);
    if (problem) return;
    setSaving(true);
    try {
      updateUser(await sessionApi.updateDisplayName(name.trim()));
      navigate(destination, { replace: true });
    } catch {
      setSaveError(t("profileName.saveFailed"));
    } finally {
      setSaving(false);
    }
  };

  const switchAccount = async () => {
    await signOut();
    navigate("/login", { replace: true });
  };

  // A user who already has a name has nothing to fill in here.
  if (user && !needsProfileName(user)) return <Navigate to={destination} replace />;

  return (
    <main className="relative flex min-h-dvh items-center justify-center bg-canvas px-6 py-16">
      <div className="absolute left-6 top-6 flex items-center gap-2 text-foreground sm:left-10 sm:top-8">
        <span className="flex size-6 items-center justify-center rounded-md bg-primary text-[13px] font-extrabold text-primary-foreground" aria-hidden="true">M</span>
        <span className="text-[15px] font-bold tracking-tight">Memoru</span>
      </div>

      <section className="flex w-full max-w-[392px] flex-col items-center gap-7 text-center" aria-labelledby="profile-name-title">
        <div className="flex flex-col items-center gap-2.5">
          <h1 id="profile-name-title" className="text-[22px] font-bold tracking-tight text-foreground">{t("profileName.title")}</h1>
          <p className="max-w-80 text-sm leading-5 text-muted-foreground">{t("profileName.description")}</p>
        </div>

        <div className="w-full rounded-xl border border-border bg-card p-7 text-left shadow-[0_1px_2px_rgb(24_24_27_/_0.04)]">
          <form className="flex flex-col gap-4" noValidate onSubmit={(event) => void submit(event)}>
            <span className="mx-auto grid size-14 place-items-center rounded-full bg-primary/10 text-lg font-semibold text-primary" aria-hidden="true">
              {initials || <UserRound className="size-6" />}
            </span>
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-foreground" htmlFor="display-name">{t("profileName.label")}</label>
              <Input
                id="display-name"
                name="name"
                autoComplete="name"
                autoFocus
                placeholder={t("profileName.placeholder")}
                value={name}
                disabled={saving}
                aria-invalid={fieldError ? true : undefined}
                aria-describedby={fieldError ? "display-name-error" : "display-name-hint"}
                onChange={(event) => {
                  setName(event.target.value);
                  if (fieldError) setFieldError(null);
                }}
              />
              {fieldError ? (
                <p id="display-name-error" role="alert" className="text-xs text-destructive">{fieldError}</p>
              ) : (
                <p id="display-name-hint" className="text-xs text-muted-foreground">{t("profileName.hint")}</p>
              )}
            </div>
            {saveError ? <ErrorAlert message={saveError} compact /> : null}
            <Button type="submit" className="h-11 w-full" loading={saving}>{t("profileName.continue")}</Button>
          </form>
        </div>

        <div className="flex max-w-80 flex-col items-center gap-1 text-xs leading-5 text-muted-foreground">
          {user ? <p className="break-all">{t("profileName.signedInAs", { email: user.email })}</p> : null}
          <Button type="button" variant="link" className="h-auto p-0 text-xs font-medium" onClick={() => void switchAccount()}>{t("profileName.switchAccount")}</Button>
        </div>
      </section>
    </main>
  );
}
